import {
  BrandCollection,
  ContactActor,
  IBrand,
  IContact,
  IContactDetails,
  IContactNumber,
  IPatient,
  IPractice,
  IReferral,
  IStaffer,
  MentionResourceType,
  OptionalPatientDetails,
  PrimaryContact,
  ReferralActor,
  ReferralParticipant,
  ReferralPatientDetails,
  ReferralStatus,
  ReferralType,
  ReferralUrgency,
  StafferActor,
  isContactActor,
  isStaffer,
  isStafferActor,
  isWithPrimaryContact,
} from '@principle-theorem/principle-core/interfaces';
import { normaliseEmail } from '../../helpers';
import {
  AtLeast,
  DocumentReference,
  Firestore,
  Timestamp,
  WithRef,
  collectionGroupQuery,
  isSameIdentifier,
  limit,
  multiUndeleted,
  toNamedDocument,
  toNamedTypeDocument,
  toTimestamp,
  uid,
  where,
} from '@principle-theorem/shared';
import { uniqBy } from 'lodash';
import { Observable } from 'rxjs';
import { Brand } from '../brand';
import { stafferToNamedDoc } from '../common';
import { StatusHistory } from '../status-history';

export type InitReferralData = AtLeast<
  IReferral,
  | 'patient'
  | 'patientDetails'
  | 'practice'
  | 'stafferDetails'
  | 'referrer'
  | 'recipient'
  | 'externalUserEmail'
>;

export type IReferralStatusChange = Partial<
  Pick<IReferral, 'status' | 'statusHistory'>
>;

type ReferralSentFields =
  | 'emailInvitationSent'
  | 'lastNotifiedAt'
  | 'lastActivityBy'
  | 'unreadBy';

export type IReferralSentPatch = Pick<IReferral, ReferralSentFields> &
  IReferralStatusChange;

export class Referral {
  static init(overrides: InitReferralData): IReferral {
    return {
      uid: uid(),
      status: ReferralStatus.Draft,
      type: ReferralType.Outgoing,
      statusHistory: [],
      participants: [],
      unreadBy: [],
      followers: [],
      urgency: ReferralUrgency.Routine,
      deleted: false,
      ...overrides,
    };
  }

  static getStaffer(referral: WithRef<IReferral>): StafferActor | undefined {
    const isOutgoing = referral.type === ReferralType.Outgoing;
    const actor = isOutgoing ? referral.referrer : referral.recipient;
    return isStafferActor(actor) ? actor : undefined;
  }

  static getContact(referral: WithRef<IReferral>): ContactActor | undefined {
    const isOutgoing = referral.type === ReferralType.Outgoing;
    const actor = isOutgoing ? referral.recipient : referral.referrer;
    return isContactActor(actor) ? actor : undefined;
  }

  static all$(brand: WithRef<IBrand>): Observable<WithRef<IReferral>[]> {
    return Firestore.query$(Brand.referralCol(brand)).pipe(multiUndeleted());
  }

  static byPatient$(
    brand: WithRef<IBrand>,
    patient: WithRef<IPatient>
  ): Observable<WithRef<IReferral>[]> {
    return Firestore.query$(
      Brand.referralCol(brand),
      where('patient.ref', '==', patient.ref)
    ).pipe(multiUndeleted());
  }

  static contactReferrals$(
    contact: WithRef<IContact>,
    brand: WithRef<IBrand>
  ): Observable<WithRef<IReferral>[]> {
    return Firestore.query$(
      Brand.referralCol(brand),
      where('participants', 'array-contains', contact.ref)
    ).pipe(multiUndeleted());
  }

  static byContact$(
    referralUid: string,
    email: string
  ): Observable<WithRef<IReferral>[]> {
    return Firestore.query$(
      collectionGroupQuery<IReferral>(BrandCollection.Referrals),
      where('uid', '==', referralUid),
      where('externalUserEmail', '==', normaliseEmail(email)),
      where('status', '!=', ReferralStatus.Draft),
      limit(1)
    ).pipe(multiUndeleted());
  }

  static byContactAll$(email: string): Observable<WithRef<IReferral>[]> {
    return Firestore.query$(
      collectionGroupQuery<IReferral>(BrandCollection.Referrals),
      where('externalUserEmail', '==', normaliseEmail(email)),
      where('status', '!=', ReferralStatus.Draft)
    ).pipe(multiUndeleted());
  }

  static participantToNamedDoc(
    participant: ReferralParticipant
  ): ReferralActor {
    if (isStaffer(participant)) {
      return toNamedTypeDocument(
        stafferToNamedDoc(participant),
        MentionResourceType.Staffer
      );
    }
    return toNamedTypeDocument(
      toNamedDocument(participant),
      MentionResourceType.Contact
    );
  }

  static stafferDetails(
    referrer: WithRef<IStaffer>,
    practice: WithRef<IPractice>
  ): IContactDetails {
    return {
      name: referrer.user.name,
      address: practice.address,
      email: practice.email,
      phone: practice.phone,
    };
  }

  static hasUnreadChanges(
    referral: WithRef<IReferral>,
    participant: ReferralActor
  ): boolean {
    if (!referral.lastActivityBy) {
      return false;
    }

    const isUserInvolved =
      Referral.isParticipant(referral, participant) ||
      Referral.isFollower(referral, participant);
    if (!isUserInvolved) {
      return false;
    }

    if (isSameIdentifier(referral.lastActivityBy, participant)) {
      return false;
    }

    return referral.unreadBy.some((reader) =>
      isSameIdentifier(reader, participant)
    );
  }

  static isParticipant(
    referral: WithRef<IReferral>,
    participant: ReferralActor
  ): boolean {
    return referral.participants.some((referralParticipant) =>
      isSameIdentifier(referralParticipant, participant)
    );
  }

  static isFollower(
    referral: WithRef<IReferral>,
    actor: ReferralActor
  ): boolean {
    return referral.followers.some((follower) =>
      isSameIdentifier(follower, actor)
    );
  }

  /**
   * Builds the patch that marks a referral as sent: the sent-state fields plus,
   * only when the referral is still a Draft, a Sent status change. For an
   * already-sent referral the `status` and `statusHistory` keys are omitted
   * entirely (not set to `undefined`) so the patch never rewrites history.
   */
  static buildSentPatch(
    referral: WithRef<IReferral>,
    actor: ReferralActor,
    now: Timestamp = toTimestamp()
  ): IReferralSentPatch {
    // Only a Draft advances to Sent; a Received or Completed referral must not
    // be regressed back to Sent.
    const statusChange =
      referral.status === ReferralStatus.Draft
        ? Referral.buildStatusChange(referral, ReferralStatus.Sent, now)
        : {};
    return {
      emailInvitationSent: true,
      lastNotifiedAt: now,
      lastActivityBy: actor,
      unreadBy: Referral.buildUnreadBy(referral, actor),
      ...statusChange,
    };
  }

  /**
   * Builds the `{ status, statusHistory }` pair for advancing a referral to a
   * new status, appending the transition to its history. Returns an empty
   * object when the status is unchanged.
   */
  static buildStatusChange(
    referral: WithRef<IReferral>,
    status: ReferralStatus,
    now: Timestamp = toTimestamp()
  ): IReferralStatusChange {
    if (referral.status === status) {
      return {};
    }
    return {
      status,
      statusHistory: StatusHistory.add(status, referral.statusHistory, now),
    };
  }

  /**
   * Derives who should still see the referral as unread after an actor touches
   * it: every participant and follower, de-duplicated by ref path, excluding
   * the acting user.
   */
  static buildUnreadBy(
    referral: WithRef<IReferral>,
    actor: ReferralActor
  ): DocumentReference<IStaffer | IContact>[] {
    const participantRefs = referral.participants ?? [];
    const followerRefs = referral.followers;
    const allRefs = uniqBy(
      [...participantRefs, ...followerRefs],
      (ref) => ref.path
    );
    return allRefs.filter((ref) => !isSameIdentifier(ref, actor));
  }

  /**
   * Reports whether a sent-patch actually advances the referral's status, so
   * callers only record a status-changed interaction on a real transition.
   */
  static hasStatusTransition(
    referral: WithRef<IReferral>,
    patch: IReferralSentPatch
  ): boolean {
    return !!patch.status && patch.status !== referral.status;
  }

  static buildPatientDetails(
    patient: WithRef<IPatient>,
    primaryContact: PrimaryContact | undefined,
    contactNumbers: IContactNumber[] | undefined,
    details?: Partial<ReferralPatientDetails>
  ): ReferralPatientDetails {
    const includeAddress = details?.includeAddress ?? true;
    const includeEmail = details?.includeEmail ?? true;
    const includePhone = details?.includePhone ?? true;

    return {
      name: patient.name,
      dateOfBirth: patient.dateOfBirth,
      primaryContact: primaryContact ?? undefined,
      address: includeAddress ? patient.address : undefined,
      email: includeEmail ? patient.email : undefined,
      contactNumbers: includePhone ? contactNumbers : undefined,
      includePhone: includePhone,
      includeEmail: includeEmail,
      includeAddress: includeAddress,
    };
  }

  static applyPatientDetailsOptions(
    patient: WithRef<IPatient>,
    options: OptionalPatientDetails,
    contactNumbers?: IContactNumber[]
  ): Partial<ReferralPatientDetails> {
    return {
      includeEmail: options.includeEmail,
      includeAddress: options.includeAddress,
      includePhone: options.includePhone,
      email: options.includeEmail ? patient.email : undefined,
      address: options.includeAddress ? patient.address : undefined,
      contactNumbers: options.includePhone ? contactNumbers : undefined,
    };
  }

  static getPrimaryContact(
    patient: WithRef<IPatient>
  ): PrimaryContact | undefined {
    return isWithPrimaryContact(patient) ? patient.primaryContact : undefined;
  }

  static storagePath(referral: WithRef<IReferral>): string {
    return referral.ref.path;
  }
}
